import React, { Component } from 'react';
import { connect } from "react-redux";
import { withRouter  } from 'react-router';
import { getTranslate } from 'react-localize-redux';
import { getLoanAndAgreementDetails, getAdditionalAgreement } from './action';
import { Card, Table, Button, Row, Col, Typography, Spin, message } from 'antd';
import ErrorCard from '../../components/errorCard/errorCard';
import './loanAndAgreementDetails.less';
import { getMessageID } from '../../utils/uuid';
import { moneyFormat } from '../../common/constants';
import * as numeral from "numeral";


const { Text } = Typography;

@connect((state)=>{
  const { localize } = state;
  return {
    translate: getTranslate(localize),
}})

class LoanAndAgreementDetails extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loanDetails: null,
            agreementDetails: null,
            loading: true,
            error: null,
            loadingAdditionalAgreement: false,
        };
    }

    componentDidMount() {
        this._isMounted = true;
        this.fetchLoanDetails();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    fetchLoanDetails = async () => {
        const { application_id } = this.props.match.params;
        try {
            const lenderId = this.props.location.state?.lenderId ?? undefined;
            const { from } = this.props.location.state || { from: '/loan' };
            const applicationStatus = this.props.location.state?.status;
            const response = await getLoanAndAgreementDetails({ los_application_id: application_id, msg_id: getMessageID(), lender_id: lenderId });
            if (!response.loan_details && !response.agreement_details) {
                this.setState({ error: new Error('Loan and agreement details are not available'), loading: false });
            } else if (!response.loan_details) {
                if (applicationStatus && applicationStatus.toLowerCase() === 'approved') {
                    // Approved but loan not yet disbursed — show not-disbursed page
                    this.setState({ agreementDetails: response.agreement_details, loading: false });
                } else {
                    // All other states — auto-download agreement and navigate back
                    await this.handleAgreementClick(response.agreement_details.pre_signed_url);
                    this.props.history.push(from);
                }
            } else {
                this.setState({ loanDetails: response.loan_details, agreementDetails: response.agreement_details, loading: false });
            }
        } catch (err) {
            this.setState({ error: err, loading: false });
        }
    };

    handleAgreementClick = async (url) => {
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/octet-stream'
                }
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
            const newWindow = window.open(blobUrl, '_blank');
            if (newWindow) {
                newWindow.focus();
            }
        } catch (err) {
            console.error('Error fetching agreement:', err);
            message.error('Error opening agreement');
        }
    };

    handleAdditionalAgreementClick = async () => {
        const { application_id } = this.props.match.params;
        const lenderId = this.props.location.state?.lenderId ?? undefined;
        this.setState({ loadingAdditionalAgreement: true });
        try {
            const response = await getAdditionalAgreement({ application_id, los_application_id: application_id, msg_id: getMessageID(), lender_id: lenderId });
            if (response.status && response.status !== 'generated') {
                message.info('Funding agreement is still being generated. Please check back shortly.');
                return;
            }
            await this.handleAgreementClick(response.pre_signed_url);
        } catch (err) {
            console.error('Error fetching additional agreement:', err);
            if (err?.status === 404) {
                message.error('Funding agreement not available');
            } else {
                message.error('Error opening funding agreement');
            }
        } finally {
            if (this._isMounted) {
                this.setState({ loadingAdditionalAgreement: false });
            }
        }
    };

    renderLoading() {
        return (
            <div className="loading-container">
                <Spin size="large" />
            </div>
        );
    }

    renderError() {
        const { error } = this.state;
        return (
            <div className="error-container">
                <ErrorCard
                    title="Error fetching loan details"
                    caption={error.message}
                />
            </div>
        );
    }

    renderNotDisbursed(title) {
        const { translate } = this.props;
        return (
            <Card title={<span className="header-text">{title}</span>} className="not-disbursed-card">
                <div className="not-disbursed-content">
                    <Text className="not-disbursed-text">
                        {translate("loanAndAgreementDetail.loan_not_disbursed")}
                    </Text>
                </div>
            </Card>
        );
    }

    renderAgreementRows() {
        const { agreementDetails, loadingAdditionalAgreement } = this.state;
        const { translate } = this.props;
        return (
            <div className="agreement-rows">
                {agreementDetails && (
                    <div className="agreement-row">
                        <Text strong className="agreement-row-label">
                            {translate("loanAndAgreementDetail.loan_agreement_label")}
                        </Text>
                        <Button
                            className="agreement-view-button"
                            type="default"
                            onClick={() => this.handleAgreementClick(agreementDetails.pre_signed_url)}
                        >
                            {translate("loanAndAgreementDetail.agreement_link")}
                        </Button>
                    </div>
                )}
                <div className="agreement-row">
                    <Text strong className="agreement-row-label">
                        {translate("loanAndAgreementDetail.funding_agreement_label")}
                    </Text>
                    <Button
                        className="agreement-view-button"
                        type="default"
                        onClick={this.handleAdditionalAgreementClick}
                        loading={loadingAdditionalAgreement}
                    >
                        {translate("loanAndAgreementDetail.agreement_link")}
                    </Button>
                </div>
            </div>
        );
    }

    renderLoanDetails() {
        const { loanDetails } = this.state;
        const { translate } = this.props;

        if (!loanDetails) {
            return this.renderNotDisbursed(translate("loanAndAgreementDetail.loan_details_header"));
        }
        return (
            <Card title={<span className="header-text">{translate("loanAndAgreementDetail.loan_details_header")}</span>} className='loan-detail-card'>
                <Row gutter={16} className='detail-text'>
                    <Col span={12}>
                        <p><Text strong>{translate("loanAndAgreementDetail.loan_id")}</Text> {loanDetails.loan_id}</p>
                        <p><Text strong>{translate("loanAndAgreementDetail.total_loan_amount")}</Text> {numeral(loanDetails.total_loan_amount).format(moneyFormat)}</p>
                        <p><Text strong>{translate("loanAndAgreementDetail.total_overdue_amount")}</Text> {numeral(loanDetails.total_overdue_amount).format(moneyFormat)}</p>
                        <p><Text strong>{translate("loanAndAgreementDetail.total_outstanding_amount")}</Text> {numeral(loanDetails.total_outstanding_amount).format(moneyFormat)}</p>
                    </Col>
                    <Col span={12}>
                        <p><Text strong>{translate("loanAndAgreementDetail.total_repaid_amount")}</Text> {numeral(loanDetails.total_repaid_amount).format(moneyFormat)}</p>
                        <p><Text strong>{translate("loanAndAgreementDetail.loan_status")}</Text> {translate(`loanAndAgreementDetail.statuses.${loanDetails.loan_status}`)}</p>
                        <p><Text strong>{translate("loanAndAgreementDetail.dpd")}</Text> {loanDetails.dpd}</p>
                    </Col>
                </Row>
            </Card>
        );
    }

    renderAgreementDetails() {
        const { translate } = this.props;
        return (
            <Card title={<span className="header-text">{translate("loanAndAgreementDetail.agreement_details_header")}</span>} className='agreement-detail-card'>
                {this.renderAgreementRows()}
            </Card>
        );
    }

    renderLoanRepaymentHistory() {
        const { loanDetails } = this.state;
        const { translate } = this.props;

        if (!loanDetails) {
            return this.renderNotDisbursed(translate("loanAndAgreementDetail.loan_repayment_header"));
        }
        const columns = [
            { title: translate("loanAndAgreementDetail.transaction_id"), dataIndex: 'transaction_id', key: 'transaction_id' },
            { title: translate("loanAndAgreementDetail.transaction_date"), dataIndex: 'date', key: 'date', render: date => new Date(date).toLocaleString() },
            { title: translate("loanAndAgreementDetail.transaction_amount"), dataIndex: 'amount', key: 'amount', render: amount => numeral(amount).format(moneyFormat) },
            { title: translate("loanAndAgreementDetail.principal_adjusted"), dataIndex: 'principal_adjusted', key: 'principal_adjusted', render: principal_adjusted => numeral(principal_adjusted).format(moneyFormat) },
            { title: translate("loanAndAgreementDetail.fee_adjusted"), dataIndex: 'fee_adjusted', key: 'fee_adjusted', render: fee_adjusted => numeral(fee_adjusted).format(moneyFormat) },
            { title: translate("loanAndAgreementDetail.penalty_adjusted"), dataIndex: 'penalty_adjusted', key: 'penalty_adjusted', render: penalty_adjusted => numeral(penalty_adjusted).format(moneyFormat) },
            { title: translate("loanAndAgreementDetail.interest_adjusted"), dataIndex: 'interest_adjusted', key: 'interest_adjusted', render: interest_adjusted => numeral(interest_adjusted).format(moneyFormat) },
        ];

        return (
            <Card title={<span className="header-text">{translate("loanAndAgreementDetail.loan_repayment_header")}</span>}>
                <Table
                    columns={columns}
                    dataSource={loanDetails.loan_repayment_history}
                    rowKey="transaction_id"
                    scroll={{ x: true }}
                    pagination={false}
                />
            </Card>
        );
    }

    render() {
        const { loading, error } = this.state;

        if (loading) {
            return this.renderLoading();
        }

        if (error) {
            return this.renderError();
        }

        return (
            <div>
                {this.renderLoanDetails()}
                {this.renderAgreementDetails()}
                {this.renderLoanRepaymentHistory()}
            </div>
        );
    }
}

export default withRouter(LoanAndAgreementDetails);
